/**
 * The single answer to "what is this filter actually doing?".
 *
 * Five different predicates for "filter is applied" grew across the
 * portal, the filter bar and the AI panel, and none of them was pinned
 * by a test. They disagree in the cases that matter most — a filter
 * whose key is ABSENT but which still narrows the query through its
 * ``default_value``, and a filter explicitly cleared to ``""`` which
 * suppresses that default. Any surface that summarises filter state
 * (a chip, a badge count, "clear all") is lying whenever it uses a
 * predicate other than the engine's.
 *
 * The engine is the authority, not this module: the semantics below
 * mirror ``first_page_cache.effective_filters`` — itself a documented
 * mirror of ``dashboard_engine._build_where``. The mirror is enforced
 * by a shared fixture (``filter_state_contract.json``) exercised from
 * pytest AND vitest, the same technique that pins the grid heights.
 *
 * Resolution rules, verbatim from the engine:
 *   - key present with a non-empty value  → that value wins
 *   - key present with ``""``             → user cleared it; the
 *                                           default is NOT re-applied
 *   - key absent                          → ``locked`` else
 *                                           ``default_value``
 *
 * Note that an active value OVERRIDES ``locked``: at the engine level
 * ``locked`` is a fallback, not an enforcement (the UI is what stops a
 * locked control from emitting a value). Surfaces must not imply
 * otherwise.
 */

import type { DashboardFilter } from "./types";

/** Mirror of ``first_page_cache._DATE_FILTER_TYPES``. */
const DATE_FILTER_TYPES = new Set(["date_range", "date_relative", "date_slider"]);

/** How a filter arrived at the value the engine will query with. */
export type FilterStateKind =
  /** ``locked`` supplied the value and the user sent nothing. */
  | "locked"
  /** The user picked this value. */
  | "explicit"
  /** Nobody touched it; the report's ``default_value`` applies. */
  | "default"
  /** The user cleared a filter that HAD a default, suppressing it. */
  | "cleared"
  /** Nothing constrains this filter — the "All" case. */
  | "all";

export interface FilterState {
  key: string;
  kind: FilterStateKind;
  /**
   * What the engine will filter by, or ``null`` when the filter is
   * inert. This is the ONLY field a chip should render a value from.
   */
  effectiveValue: string | null;
  /** The report-declared default (``locked`` > ``default_value``), resolved. */
  defaultValue: string | null;
}

/**
 * Serialize a resolved date window the way Python's
 * ``json.dumps(obj, sort_keys=True)`` does.
 *
 * ``JSON.stringify`` emits no spaces and preserves insertion order,
 * while Python's default separators are ``", "`` / ``": "`` and
 * ``sort_keys`` reorders. The two strings must match byte-for-byte
 * because this value lands in the first-page cache KEY — a mismatch
 * silently splits the cache in two and serves cold queries forever.
 */
function pyJsonDateWindow(start: string, end: string): string {
  return `{"end": "${end}", "start": "${start}"}`;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Mirror of ``first_page_cache._resolve_relative_date_range``:
 * ``[today - length <unit>, today)`` — today is NOT included, like the
 * engine's raw ``{"relative": …}`` window. The engine reads a 10-char
 * ISO ``end`` as an inclusive day, so the resolved ``end`` is yesterday.
 * Months are approximated as 30 days — deliberately, because that is
 * what the engine does.
 */
function resolveRelativeDateRange(
  relative: Record<string, unknown>,
  today: Date,
): string {
  const unit = String(relative["unit"] ?? "days").toLowerCase();
  const rawLength = relative["length"];
  let length = Number(rawLength);
  // Python's ``int()`` raises on junk and the engine catches it to 0;
  // ``Number("abc")`` is NaN, so normalise to the same 0.
  if (!Number.isFinite(length)) length = 0;
  length = Math.trunc(length);
  // Inclusive last day of the window: yesterday (today is excluded).
  const end = new Date(today.getTime());
  end.setUTCDate(end.getUTCDate() - 1);

  if (unit === "years") {
    // Calendar-exact, matching the engine: the same day N years back,
    // so the window does not drift by the leap days that 365 × N
    // accumulates. 29 Feb lands on 28 Feb when the target year has no
    // 29th — setUTCFullYear would silently roll it into 1 March.
    const start = new Date(today.getTime());
    const wantMonth = start.getUTCMonth();
    start.setUTCFullYear(start.getUTCFullYear() - length);
    if (start.getUTCMonth() !== wantMonth) start.setUTCDate(0);
    return pyJsonDateWindow(isoDate(start), isoDate(end));
  }

  let days: number;
  if (unit === "weeks") days = length * 7;
  else if (unit === "months") days = length * 30;
  else days = length;

  const start = new Date(today.getTime());
  start.setUTCDate(start.getUTCDate() - days);
  return pyJsonDateWindow(isoDate(start), isoDate(end));
}

/**
 * Mirror of ``first_page_cache._resolve_default_value``.
 *
 * Returns the on-the-wire form of a filter's default, or ``null`` when
 * it declares none. Only a ``{"relative": …}`` payload on a date-window
 * filter is expanded; presets and absolute windows travel verbatim.
 */
export function resolveFilterDefault(
  filt: DashboardFilter,
  today: Date,
): string | null {
  const raw = filt.locked || filt.default_value;
  if (!raw) return null;
  if (!DATE_FILTER_TYPES.has(filt.type ?? "select")) return raw;

  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    // Engine behaviour on malformed JSON: hand back the raw string and
    // let the decoder downstream skip the filter. Never invent a window.
    return raw;
  }
  if (
    payload !== null &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    "relative" in (payload as Record<string, unknown>)
  ) {
    const rel = (payload as Record<string, unknown>)["relative"];
    if (rel !== null && typeof rel === "object" && !Array.isArray(rel)) {
      return resolveRelativeDateRange(rel as Record<string, unknown>, today);
    }
  }
  return raw;
}

/** Mirror of ``first_page_cache.resolve_default_filters``. */
export function resolveDefaultFilters(
  filters: ReadonlyArray<DashboardFilter>,
  today: Date,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const filt of filters) {
    const resolved = resolveFilterDefault(filt, today);
    if (resolved !== null) out[filt.key] = resolved;
  }
  return out;
}

/**
 * Mirror of ``first_page_cache.effective_filters`` — the dict the
 * engine effectively queries with.
 */
export function effectiveFilters(
  filters: ReadonlyArray<DashboardFilter>,
  active: Readonly<Record<string, string>>,
  today: Date,
): Record<string, string> {
  const defaults = resolveDefaultFilters(filters, today);
  const out: Record<string, string> = {};
  for (const filt of filters) {
    const key = filt.key;
    if (Object.prototype.hasOwnProperty.call(active, key)) {
      const value = active[key];
      if (value) out[key] = value;
      // else: user cleared → drop entirely, no default fallback
    } else if (key in defaults) {
      out[key] = defaults[key]!;
    }
  }
  return out;
}

/** Resolve one filter to its full state. */
export function filterState(
  filt: DashboardFilter,
  active: Readonly<Record<string, string>>,
  today: Date,
): FilterState {
  const key = filt.key;
  const defaultValue = resolveFilterDefault(filt, today);
  const present = Object.prototype.hasOwnProperty.call(active, key);
  const raw = present ? active[key] : undefined;

  if (present && raw) {
    return { key, kind: "explicit", effectiveValue: raw, defaultValue };
  }
  if (present) {
    // Explicit "" — the user cleared it. Only meaningful as its own
    // state when a default existed to suppress; otherwise it is just
    // "All" arrived at by a different route.
    return {
      key,
      kind: defaultValue !== null ? "cleared" : "all",
      effectiveValue: null,
      defaultValue,
    };
  }
  if (defaultValue !== null) {
    return {
      key,
      // ``locked`` and ``default_value`` both land here; distinguishing
      // them lets a chip render the padlock without re-reading config.
      kind: filt.locked ? "locked" : "default",
      effectiveValue: defaultValue,
      defaultValue,
    };
  }
  return { key, kind: "all", effectiveValue: null, defaultValue };
}

/** Every filter's state, in declaration order. */
export function filterStates(
  filters: ReadonlyArray<DashboardFilter>,
  active: Readonly<Record<string, string>>,
  today: Date,
): FilterState[] {
  return filters.map((f) => filterState(f, active, today));
}

/**
 * Does this filter currently constrain the query?
 *
 * This is the predicate a CHIP uses to decide between showing a value
 * and showing "All" — a date range sitting on its report default is
 * absolutely filtering, even though the user never touched it.
 */
export function isFilterActive(state: FilterState): boolean {
  return state.effectiveValue !== null;
}

/**
 * Has the user moved this filter away from what the report declares?
 *
 * Distinct from :func:`isFilterActive` on purpose, and the distinction
 * is the whole reason the old predicates disagreed. A badge count and
 * a "clear all" affordance want THIS one: every report would otherwise
 * open reading "3 filters applied" because its date window has a
 * default, which is noise rather than information.
 */
export function hasUserOverride(state: FilterState): boolean {
  if (state.kind === "cleared") return true;
  if (state.kind === "explicit") return state.effectiveValue !== state.defaultValue;
  return false;
}

/** How many filters the user has actually moved. Drives the badge. */
export function countUserOverrides(states: ReadonlyArray<FilterState>): number {
  return states.filter(hasUserOverride).length;
}

/**
 * The value a required filter should be seeded with, given the options
 * the cascade returned. Extracted verbatim from the portal's inline
 * three-tier fallback so the same rule can run outside that effect:
 *
 *   1. ``default_value``, when it exists among the options;
 *   2. a prefix match on the default's first two words, upper-cased —
 *      tolerance for naming drift in the data (``"OFFICE DEPOT INC."``
 *      still finds ``"OFFICE DEPOT INCORPORATED"``);
 *   3. ``options[0]``, so the dashboard never opens stuck on
 *      "pick a filter".
 *
 * Returns ``null`` when nothing can be seeded (no options), and for
 * date-window filters, which have no discrete options to choose from —
 * their default is JSON and matching it against option strings would
 * pick garbage.
 */
export function resolveSeedValue(
  filt: DashboardFilter,
  options: ReadonlyArray<string>,
): string | null {
  if (DATE_FILTER_TYPES.has(filt.type ?? "select")) return null;
  if (options.length === 0) return null;

  const isMulti = filt.type === "multi_select" || filt.type === "chip";
  if (isMulti) {
    if (filt.default_value) {
      try {
        const decoded: unknown = JSON.parse(filt.default_value);
        if (Array.isArray(decoded)) {
          const available = new Set(options);
          const matches = decoded
            .map(String)
            .filter((value, index, values) =>
              available.has(value) && values.indexOf(value) === index,
            );
          if (matches.length > 0) return JSON.stringify(matches);
        }
      } catch {
        // A malformed multi-select default is not a scalar option. Fall
        // through to a valid one-item JSON list derived from the cascade.
      }
    }
    return JSON.stringify([options[0]!]);
  }

  const dflt = filt.default_value;
  if (dflt && options.includes(dflt)) return dflt;
  if (dflt) {
    const words = dflt.split(" ").filter(Boolean);
    const prefix = words.slice(0, 2).join(" ").toUpperCase();
    const partial = options.find((o) => o.toUpperCase().startsWith(prefix));
    if (partial) return partial;
  }
  return options[0] ?? null;
}
